import { useState, useEffect, useCallback } from 'react';
import Accounts from "../AccountTable/Liste";
import TotalCount from './TotalCount';
import useSelectedDomain from '../../../service/Hooks/useSelectedDomain';
import apiClient from '../../../service/api/api';
import { formatAmount } from '../../../service/utils/orderUtils';
import CounterCardsSkeleton from '../../../skeleton/CounterCardsSkeleton';

const ConnectionStatus = ({ isOffline, onRetry }) => {
  if (!isOffline) return null;
  return (
    <div className="connection-status-banner visible">
      <div className="status-content">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12.55a11 11 0 0 1 14.08 0"></path>
          <path d="M1.42 9a16 16 0 0 1 21.16 0"></path>
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
          <line x1="12" y1="20" x2="12.01" y2="20"></line>
        </svg>
        <span>Mode hors ligne</span>
        <button onClick={onRetry} className="btn-retry">Réessayer</button>
      </div>
    </div>
  );
};

const CounterCard = ({ icon, label, count, subtitle, type, isOffline }) => (
  <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
    <div className={`card counter modern-card ${type}-card ${isOffline ? 'offline-mode' : ''}`}>
      <div className="card-glow"></div>
      <div className="card-background"></div>
      {isOffline && <div className="offline-indicator"><span>Hors ligne</span></div>}
      <div className="card-body text-center">
        <div className="card-element-spacing">
          <div className="card-box-icon"><div className="icon-shine"></div>{icon}</div>
        </div>
        <div className="card-element-spacing">
          <p className="card-label"><span className="label-text text-uppercase">{label}</span><span className="label-dot"></span></p>
        </div>
        <div className="card-element-spacing">
          <h2 className="card-count">
            <span className={`count-number glow-text ${isOffline ? 'text-muted' : ''}`}>
              {isOffline ? '--' : formatAmount(count || 0)}
            </span>
          </h2>
        </div>
        <div className="card-element-spacing">
          <div className="card-subtitle"><span className="subtitle-icon"></span>{isOffline ? 'Connexion interrompue' : subtitle}</div>
        </div>
      </div>
      <div className="card-corner"></div>
    </div>
  </div>
);

const ListUser = () => {
    const selectedDomain = useSelectedDomain();

    // ✅ État loading SÉPARÉ de counts — skeleton uniquement sur le premier chargement
    const [loading, setLoading]           = useState(true);
    const [counts, setCounts]             = useState([]);
    const [isOffline, setIsOffline]       = useState(false);
    const [refreshTotalKey, setRefreshTotalKey] = useState(0);

    const fetchCounts = useCallback(async () => {
        if (!selectedDomain?.id) return;

        // ✅ Ne montre le skeleton QUE si on n'a encore aucune donnée
        if (counts.length === 0) setLoading(true);

        try {
            const { data } = await apiClient.get('/api/accounts/countbytype', {
                params: { domainId: selectedDomain.id }
            });
            setCounts(data ?? []);
            setIsOffline(false);
        } catch (err) {
            const isConnectionError =
                !err.response ||
                err.code === 'ECONNABORTED' ||
                err.message?.includes('Network Error') ||
                err.message?.includes('Failed to fetch');
            setIsOffline(isConnectionError);
            // En cas d'erreur, on garde les données déjà affichées s'il y en a
            if (counts.length === 0) setCounts([]);
        } finally {
            // ✅ On arrête TOUJOURS le loading, même en cas d'erreur
            setLoading(false);
        }
    }, [selectedDomain?.id]); // counts intentionnellement absent pour éviter boucle infinie

    useEffect(() => {
        fetchCounts();
    }, [fetchCounts]);

    // Réessayer depuis le banner hors ligne
    const handleRetry = () => {
        setIsOffline(false);
        fetchCounts();
    };

    useEffect(() => {
        const handleOnline = () => {
            if (isOffline) fetchCounts();
        };
        window.addEventListener('online', handleOnline);
        return () => window.removeEventListener('online', handleOnline);
    }, [isOffline, fetchCounts]);

    const handleAccountDeleted = (accountId, accountTypeName) => {
        if (isOffline || !counts.length) return;
        setCounts(prev =>
            prev.map(c =>
                c.accountTypeName === accountTypeName
                    ? { ...c, count: Math.max(0, c.count - 1) }
                    : c
            )
        );
        setRefreshTotalKey(k => k + 1);
    };

    const getCount = type => counts?.find(c => c.accountTypeName === type)?.count || 0;
    const particulierCount  = getCount('Particulier');
    const opCount           = getCount('Opc') + getCount('Opcvm') + getCount('Fia');
    const entrepriseCount   =
        getCount('Entreprise') + getCount('Autres Entreprises') +
        getCount('Banques')    + getCount('Assurance') +
        getCount('autres')     + getCount('Association');

    return (
        <>
            <ConnectionStatus isOffline={isOffline} onRetry={handleRetry} />

            {/* ✅ Skeleton UNIQUEMENT pendant le tout premier chargement */}
            {loading ? (
                <CounterCardsSkeleton />
            ) : (
                <div className="cards-section-wrapper" style={{ marginTop: '30px' }}>
                    <div className="row gy-4 gx-3 justify-content-start">
                        <div className="col-xl-12">
                            <div className="row g-4 modern-cards-container">
                                <CounterCard
                                    type="particulier"
                                    label="Particuliers"
                                    count={particulierCount}
                                    subtitle="Comptes individuels"
                                    isOffline={isOffline}
                                    icon={<svg width="72" height="72" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" fill="url(#particulier-gradient)" stroke="url(#particulier-gradient)" strokeWidth="1.5"/><path d="M5.33788 18.3206C5.99897 15.5269 8.77173 14 12.0002 14C15.2286 14 18.0014 15.5269 18.6625 18.3206C18.7956 18.8611 18.5363 19.4266 18.0313 19.6697C16.6464 20.337 14.8964 21 12.0002 21C9.10396 21 7.35398 20.337 5.96906 19.6697C5.46406 19.4266 5.20478 18.8611 5.33788 18.3206Z" fill="url(#particulier-gradient)" stroke="url(#particulier-gradient)" strokeWidth="1.5"/><defs><linearGradient id="particulier-gradient" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4F46E5" /><stop offset="50%" stopColor="#6366F1" /><stop offset="100%" stopColor="#818CF8" /></linearGradient></defs></svg>}
                                />
                                <CounterCard
                                    type="association"
                                    label="Opc"
                                    count={opCount}
                                    subtitle="Organisme de Placement Collectif"
                                    isOffline={isOffline}
                                    icon={<svg width="72" height="72" viewBox="0 0 24 24" fill="none"><path d="M17 20H22V18C22 16.3431 20.6569 15 19 15C18.0444 15 17.1931 15.4468 16.6438 16.1429M17 20H7M17 20V18C17 17.3438 16.8736 16.717 16.6438 16.1429M7 20H2V18C2 16.3431 3.34315 15 5 15C5.95561 15 6.80686 15.4468 7.35625 16.1429M7 20V18C7 17.3438 7.12642 16.717 7.35625 16.1429M7.35625 16.1429C8.0935 14.301 9.89482 13 12 13C14.1052 13 15.9065 14.301 16.6438 16.1429M15 7C15 8.65685 13.6569 10 12 10C10.3431 10 9 8.65685 9 7C9 5.34315 10.3431 4 12 4C13.6569 4 15 5.34315 15 7ZM21 7C21 8.65685 19.6569 10 18 10C16.3431 10 15 8.65685 15 7C15 5.34315 16.3431 4 18 4C19.6569 4 21 5.34315 21 7ZM3 7C3 8.65685 4.34315 10 6 10C7.65685 10 9 8.65685 9 7C9 5.34315 7.65685 4 6 4C4.34315 4 3 5.34315 3 7Z" stroke="url(#association-gradient)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><defs><linearGradient id="association-gradient" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#10B981" /><stop offset="50%" stopColor="#34D399" /><stop offset="100%" stopColor="#6EE7B7" /></linearGradient></defs></svg>}
                                />
                                <CounterCard
                                    type="entreprise"
                                    label="Entreprises"
                                    count={entrepriseCount}
                                    subtitle="Sociétés, Banques, Association & institutions"
                                    isOffline={isOffline}
                                    icon={<svg width="72" height="72" viewBox="0 0 24 24" fill="none"><path d="M3 21H21M5 21V7L12 3L19 7V21M9 21V13H15V21" stroke="url(#entreprise-gradient)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 9H10M9 13H10M14 9H15M14 13H15" stroke="url(#entreprise-gradient)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><defs><linearGradient id="entreprise-gradient" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#F59E0B" /><stop offset="50%" stopColor="#FBBF24" /><stop offset="100%" stopColor="#FCD34D" /></linearGradient></defs></svg>}
                                />
                                <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12">
                                    <TotalCount refreshKey={refreshTotalKey} isOffline={isOffline} loading={false} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Le tableau des comptes s'affiche TOUJOURS, indépendamment du loading des cartes */}
            <div className="accounts-section-wrapper">
                <Accounts
                    onAccountDeleted={handleAccountDeleted}
                    onCreateSuccess={() => fetchCounts()}
                    isParentOffline={isOffline}
                />
            </div>
        </>
    );
};

export default ListUser;